import * as R from 'ramda'
import { useEffect, useState } from 'react'
import mime from 'mime'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

export const getFileExtension = (filename: string) =>
  R.compose(R.last, R.split('.'))(filename) as string

export const getFileTitle = (filename?: string) => {
  const parts = filename?.split('.') || []
  return parts.length === 1 ? parts[0] : R.init(parts).join('.')
}

const SIZE_UNITS = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
}

export const formatFileSize = (
  fileSize: number,
  sizeUnit: keyof typeof SIZE_UNITS | 'auto' = 'auto',
  includeSizeUnit = true,
) => {
  const { size, unit }: { size: number; unit: keyof typeof SIZE_UNITS } =
    sizeUnit !== 'auto'
      ? { size: fileSize / SIZE_UNITS[sizeUnit], unit: sizeUnit }
      : fileSize < SIZE_UNITS.KB
        ? { size: fileSize, unit: 'B' }
        : fileSize < SIZE_UNITS.MB
          ? {
              size: fileSize / SIZE_UNITS.KB,
              unit: 'KB',
            }
          : fileSize < SIZE_UNITS.GB
            ? {
                size: fileSize / SIZE_UNITS.MB,
                unit: 'MB',
              }
            : {
                size: fileSize / SIZE_UNITS.GB,
                unit: 'GB',
              }

  const formattedSizeNumber = toLocale(
    (size < 0.1 ? 0.1 : size).toLocaleString('en-US', {
      maximumFractionDigits: 1,
    }),
  )

  const finalUnit = sizeUnit === 'auto' ? unit : sizeUnit

  return {
    unit: finalUnit,
    sizeText: `${formattedSizeNumber}${
      unit && includeSizeUnit ? ` ${t(`common.${finalUnit}`)}` : ''
    }`,
  }
}

export const messageTypeByFileType = (type: File['type']) => {
  // TODO: Update this function and replace with supported types
  if (type === 'image/jpeg' || type === 'image/png' || type === 'image/webp')
    return 'IMAGE'
  if (type === 'video/mp4' || type === 'video/webm' || type === 'video/mkv')
    return 'VIDEO'
  if (type === 'audio/webm;codecs=opus') return 'VOICE'
  if (type === 'audio/mpeg' || type === 'audio/ogg' || type === 'audio/wav')
    return 'AUDIO'
  return 'FILE'
}

export function useFileObjectUrl(file?: File | null) {
  const [url, setUrl] = useState('')

  useEffect(() => {
    if (url) URL.revokeObjectURL(url)

    if (!file) setUrl('')
    else setUrl(URL.createObjectURL(file))

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])
  return url
}

export const urlToFileObject = (
  url: string,
  filename: string,
  mimetype: string,
) =>
  fetch(url)
    .then(res => res.arrayBuffer())
    .then(buf => {
      return new File([buf], filename, { type: mimetype })
    })

export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, {
    lastModified: dateWithOffset(),
    type: blob.type,
  })
}

export const convertExtensionToMimeType = (rawExt: string) => {
  const ext = rawExt.toLowerCase()
  return mime.getType(ext)
}
